import { memo, useRef, FocusEvent, useEffect, ChangeEvent } from 'react';
import { useField, useFormikContext } from 'formik';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../context/Localization/localization';
import { useKeyboardIntegration } from '../../../../context/Keyboard/keyboard';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../context/Keyboard/keyboard.types';
import { PostalCodePatternMask } from '../PostalCodePatternMask/PostalCodePatternMask';
import { KioskFormPostalCodeFieldProps } from '../FormPostalCode.types';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const KioskFormPostalCodeField = memo(({
    name,
    id,
    title,
    label,
    ariaLabel,
    disabled,
    required,
    autoFocus,
    keyboardLayout,
    shiftAfterSpace,
    shiftAfterPeriod,
    shiftAfterAmpersand,
    hideKeyboardOnClickOut,
    capsLock,
    fullWidth,
    seventyFivePercentWidth,
    tabFocusRefContext,
    onChangeCallback,
}: KioskFormPostalCodeFieldProps) => {
    const [field, meta] = useField({
        name: name,
        validate: TextFieldValidation({
            required: required ?? false,
            label,
            name,
            validationType: 'postalCode',
        }),
    });
    const { submitCount, setFieldValue } = useFormikContext();

    /**
     * A ref to the HTMLInputElement in the MUI TextField.
     */
    const inputRef = useRef<HTMLInputElement>(null);
    /**
     * The input value ref to prevent a stale state when the on-screen keyboard
     * calls any functions that use the internal value state.
     */
    const valueRef = useRef<string>(field.value ?? '');
    /**
     * The caret position ref to prevent a stale state when the on-screen keyboard
     * calls any functions that use the internal caret position state.
     */
    const caretPositionRef = useRef<number | null>(null);
    /**
     * The focus ref to prevent a stale state when the on-screen keyboard
     * calls any functions that need to know if the field is focused or not.
     */
    const isFocused = useRef<boolean>(false);
    /**
     * The on-screen keyboard input ref is used to prevent a stale state when we call to update it
     * from keyboard context functions. It is used to prevent the onChange function that recieves a
     * callback from the pattern mask from resetting the caret position incorrectly after we set it
     * using the keyboard context setValue function.
     */
    const wasOSKInput = useRef<boolean>(false);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    const {
        dealer: { primaryLanguage },
    } = useKioskState();
    const locale = useLocale();

    useEffect(() => {
        if (tabFocusRefContext) {
            tabFocusRefContext.current = inputRef.current;
        }
    }, [tabFocusRefContext]);

    useEffect(() => {
        /**
         * Updates the internal caret position when the internal MUI value finishes changing from the
         * pattern mask.
         */
        if (inputRef.current) {
            inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);

            /**
             * We don't want the pattern mask to incorrectly update the caret position,
             * so if the on-screen keyboard was used, this will not trigger.
             */
            if (wasOSKInput.current) {
                wasOSKInput.current = false;
            }
        }
    }, [inputRef.current?.value]);

    useEffect(() => {
        if (field.value !== valueRef.current) {
            valueRef.current = field.value;

            onChangeCallback && onChangeCallback({
                target: {
                    value: valueRef.current,
                },
            } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
        }

    }, [field.value, onChangeCallback]);

    useEffect(() => {
        if (!disabled && inputRef.current && isFocused.current === true) {
            isFocused.current = false;
        }
    }, [disabled]);

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const getValue = (): GetValueReturn => {
        /**
         * We initialize the wasOSKInput ref here to be true so that onChange doesn't
         * set a new caret position when it runs.
         */
        wasOSKInput.current = true;

        if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
            const selectionStart: number = inputRef.current.selectionStart;
            const selectionEnd: number = inputRef.current.selectionEnd;

            return {
                inputValue: valueRef.current,
                caretPositionStart: selectionStart,
                caretPositionEnd: selectionEnd,
            };
        }
        else {
            return {
                inputValue: valueRef.current,
                caretPositionStart: valueRef.current.length,
                caretPositionEnd: valueRef.current.length,
            };
        }
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const setValue = ({ value, caretPosition }: SetValueProps): void => {
        value = value.toLocaleUpperCase();

        caretPositionRef.current = caretPosition;
        setFieldValue(name, value);
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const onInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
        /**
         * When the postal code exceeds a length of seven, we cut out the most recent input value and
         * set the caret position to what it was prior to the input.
         */
        if (value.length > 7) {
            value = value.slice(0, caretPosition - 1) + value.slice(caretPosition);
            caretPosition = caretPosition - 1;
        }
        /**
         * If the postal code does not exceed a length of 7, we check to see if we
         * are before or after the spaces separating the first and last three characters as
         * well as if we are entering a new value or backspacing to manage the caret position's
         * transition over that space.
         */
        else {
            if (!wasBackspace && caretPosition === 4) {
                caretPosition = caretPosition + 1;
            }
            if (wasBackspace && caretPosition === 4) {
                caretPosition = caretPosition - 1;
            }
        }

        return {
            value: value,
            caretPosition: caretPosition,
        };
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const onKeyboardInput = (): void => {
        if (inputRef.current && !isFocused.current) {
            inputRef.current.focus();
        }
    };

    const { onFocus, onBlur } = useKeyboardIntegration({
        fieldValue: field.value,
        getValue: getValue,
        setValue: setValue,
        onInputChange: onInputChange,
        onKeyboardInput: onKeyboardInput,
        keyboardLayout: keyboardLayout,
        shiftAfterPeriod: shiftAfterPeriod,
        shiftAfterAmpersand: shiftAfterAmpersand,
        shiftAfterSpace: shiftAfterSpace,
        hideKeyboardOnClickOut: hideKeyboardOnClickOut,
        capsLock: capsLock,
    });

    const handleOnBlur = () => {
        onBlur();
        isFocused.current = false;
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        event.target.value = event.target.value.toLocaleUpperCase();

        /**
         * We don't want the onAccept from the mask to update anything when
         * the on-screen keyboard is used, so if we use the on-screen keyboard
         * we skip this section of code.
         */
        if (!wasOSKInput.current) {
            caretPositionRef.current = event.target.selectionEnd;
        }
        /**
         * We also don't want the onAccept from the mask to update anything here,
         * but we only want it to not do that when the length is less than three.
         * If it is greater than three, we want the mask to update the internal value
         * with the formatting since formik uses the formatting in validation.
         */
        if (valueRef.current.length > 3 || !wasOSKInput.current) {
            setFieldValue(name, event.target.value);
        }
    };

    const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        isFocused.current = true;
        onFocus(event);
    };

    return (
        <BaseTextField
            name={name}
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            inputMode={'text'}
            type={'text'}
            inputRef={inputRef}
            value={field.value ?? ''}
            error={meta.error}
            touched={meta.touched || submitCount > 0}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            capsLock={capsLock}
            onFocus={handleOnFocus}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            InputProps={{
                inputComponent: PostalCodePatternMask as any,
            }}
            inputProps={{
                maxLength: 7,
            }}
        />
    );
});